import * as Yup from "yup";
import { indianMobileSchema } from "../../helpers/mobileValidation";
import { AADHAR_CARD_REGEX, PAN_CARD_REGEX } from "../../helpers/regex";

/** Field order per wizard step — used for validation highlighting and scroll-to-error. */
export const STEP_VALIDATION_FIELDS = {
    1: ["date", "loanType", "name", "mobileNumber"],
    3: [
        "loanAmount",
        "interestPerYear",
        "loanStartDate",
        "durationYears",
        "durationMonths",
        "interestType",
        "monthlyInstallment",
        "paymentType",
        "bankAccount",
        "chequeTransactionNo",
        "firstEMIDate",
        "processingCharge",
        "processingChargeReceivedMode",
        "processingChargeBankAccount",
        "processingChargeTransactionNo",
    ],
};

// Guarantor form initial state
export const GUARANTOR_INITIAL_STATE = {
    fullName: "",
    fatherName: "",
    mobileNumber: "",
    alternateMobile: "",
    address: "",
    idProofType: "",
    idNumber: "",
    photo: null,
    signature: null,
    occupation: "",
    monthlyIncome: "",
    employerBusinessName: "",
    relationshipWithBorrower: "",
    term: "",
    termsCondition: "",
};

// JSON-driven guarantor field configuration
export const guarantorFieldConfig = [
    { name: "fullName", type: "text", required: true, labelKey: "customer.add.step2.guarantor.fullName", col: 6 },
    { name: "fatherName", type: "text", required: false, labelKey: "customer.add.step2.guarantor.fatherName", col: 6 },
    { name: "mobileNumber", type: "tel", required: true, labelKey: "customer.add.step2.guarantor.mobileNumber", col: 6, maxLength: 10, onlyDigits: true },
    { name: "alternateMobile", type: "tel", required: false, labelKey: "customer.add.step2.guarantor.alternateMobile", col: 6, maxLength: 10, onlyDigits: true },
    { name: "address", type: "textarea", required: true, labelKey: "customer.add.step2.guarantor.address", col: 12, rows: 2 },
    {
        name: "idProofType", type: "select", required: false, labelKey: "customer.add.step2.guarantor.idProofType", col: 6, options: [
            { value: "aadhaar", labelKey: "customer.add.step2.idProofTypes.aadhaar" },
            { value: "pan", labelKey: "customer.add.step2.idProofTypes.pan" },
            { value: "voterId", labelKey: "customer.add.step2.idProofTypes.voterId" },
            { value: "drivingLicense", labelKey: "customer.add.step2.idProofTypes.drivingLicense" },
            { value: "passport", labelKey: "customer.add.step2.idProofTypes.passport" },
        ]
    },
    { name: "idNumber", type: "text", required: false, labelKey: "customer.add.step2.guarantor.idNumber", col: 6 },
    { name: "photo", type: "file", required: false, labelKey: "customer.add.step2.guarantor.photo", col: 6 },
    { name: "signature", type: "file", required: false, labelKey: "customer.add.step2.guarantor.signature", col: 6 },
    { name: "occupation", type: "text", required: false, labelKey: "customer.add.step2.guarantor.occupation", col: 6 },
    { name: "monthlyIncome", type: "number", required: false, labelKey: "customer.add.step2.guarantor.monthlyIncome", col: 6, step: "0.01", min: "0" },
    { name: "employerBusinessName", type: "text", required: false, labelKey: "customer.add.step2.guarantor.employerBusinessName", col: 6 },
    { name: "relationshipWithBorrower", type: "text", required: true, labelKey: "customer.add.step2.guarantor.relationshipWithBorrower", col: 6 },
    { name: "termsCondition", type: "richtext", required: false, labelKey: "customer.add.step2.guarantor.termsCondition", col: 12, rows: 3 },
];

// JSON-driven collateral type configuration
export const collateralTypeConfig = {
    goldJewellery: {
        fields: [
            { name: "weightGrams", type: "number", required: true, labelKey: "customer.add.step2.collateralFields.weightGrams", step: "0.01", min: "0" },
            { name: "weightMilligrams", type: "number", required: false, labelKey: "customer.add.step2.collateralFields.weightMilligrams", step: "0.01", min: "0" },
            { name: "carat", type: "number", required: false, labelKey: "customer.add.step2.collateralFields.carat", step: "0.01", min: "0" },
            { name: "description", type: "text", required: false, labelKey: "customer.add.step2.collateral.description" },
            { name: "estimatedValue", type: "number", required: true, labelKey: "customer.add.step2.collateral.estimatedValue", step: "0.01", min: "0" },
            { name: "photo", type: "file", required: false, labelKey: "customer.add.step2.collateral.photo" },
            { name: "termsCondition", type: "richtext", required: false, labelKey: "customer.add.step2.collateralFields.termsCondition", rows: 3 },
        ],
    },
    silverJewellery: {
        fields: [
            { name: "weightGrams", type: "number", required: true, labelKey: "customer.add.step2.collateralFields.weightGrams", step: "0.01", min: "0" },
            { name: "weightMilligrams", type: "number", required: false, labelKey: "customer.add.step2.collateralFields.weightMilligrams", step: "0.01", min: "0" },
            { name: "description", type: "text", required: false, labelKey: "customer.add.step2.collateral.description" },
            { name: "estimatedValue", type: "number", required: true, labelKey: "customer.add.step2.collateral.estimatedValue", step: "0.01", min: "0" },
            { name: "photo", type: "file", required: false, labelKey: "customer.add.step2.collateral.photo" },
            { name: "termsCondition", type: "richtext", required: false, labelKey: "customer.add.step2.collateralFields.termsCondition", rows: 3 },
        ],
    },
    mobilePhone: {
        fields: [
            { name: "brand", type: "text", required: true, labelKey: "customer.add.step2.collateralFields.brand" },
            { name: "model", type: "text", required: true, labelKey: "customer.add.step2.collateralFields.model" },
            { name: "imei", type: "text", required: false, labelKey: "customer.add.step2.collateralFields.imei" },
            {
                name: "condition", type: "select", required: false, labelKey: "customer.add.step2.collateralFields.condition", options: [
                    { value: "new", labelKey: "customer.add.step2.conditionOptions.new" },
                    { value: "used", labelKey: "customer.add.step2.conditionOptions.used" },
                ]
            },
            { name: "estimatedValue", type: "number", required: true, labelKey: "customer.add.step2.collateral.estimatedValue", step: "0.01", min: "0" },
            { name: "termsCondition", type: "richtext", required: false, labelKey: "customer.add.step2.collateralFields.termsCondition", rows: 3 },
        ],
    },
    motorbike: {
        fields: [
            { name: "registrationNumber", type: "text", required: true, labelKey: "customer.add.step2.collateralFields.registrationNumber" },
            { name: "chassisNumber", type: "text", required: false, labelKey: "customer.add.step2.collateralFields.chassisNumber" },
            { name: "engineNumber", type: "text", required: false, labelKey: "customer.add.step2.collateralFields.engineNumber" },
            { name: "model", type: "text", required: false, labelKey: "customer.add.step2.collateralFields.model" },
            { name: "ownerName", type: "text", required: true, labelKey: "customer.add.step2.collateralFields.ownerName" },
            { name: "estimatedValue", type: "number", required: true, labelKey: "customer.add.step2.collateral.estimatedValue", step: "0.01", min: "0" },
            { name: "photo", type: "file", required: false, labelKey: "customer.add.step2.collateral.photo" },
            { name: "termsCondition", type: "richtext", required: false, labelKey: "customer.add.step2.collateralFields.termsCondition", rows: 3 },
        ],
    },
    others: {
        fields: [
            { name: "customTypeName", type: "text", required: true, labelKey: "customer.add.step2.collateralFields.customTypeName" },
            { name: "description", type: "text", required: false, labelKey: "customer.add.step2.collateral.description" },
            { name: "quantity", type: "number", required: false, labelKey: "customer.add.step2.collateralFields.quantity", min: "1" },
            { name: "estimatedValue", type: "number", required: true, labelKey: "customer.add.step2.collateral.estimatedValue", step: "0.01", min: "0" },
            { name: "photo", type: "file", required: false, labelKey: "customer.add.step2.collateral.photo" },
            { name: "termsCondition", type: "richtext", required: false, labelKey: "customer.add.step2.collateralFields.termsCondition", rows: 3 },
        ],
    },
};

export const CASTE_OPTIONS = [
    { value: "general", labelKey: "customer.add.step1.casteOptions.general" },
    { value: "obc", labelKey: "customer.add.step1.casteOptions.obc" },
    { value: "sc", labelKey: "customer.add.step1.casteOptions.sc" },
    { value: "st", labelKey: "customer.add.step1.casteOptions.st" },
    { value: "other_backward_class", labelKey: "customer.add.step1.casteOptions.otherBackwardClass" },
];

export const CASTE_VALUES = CASTE_OPTIONS.map((o) => o.value);

/** Resolve stored caste code (or legacy free-text) to a display label. */
export const getCasteLabel = (caste, t) => {
    if (!caste) return "";
    const normalized = String(caste).trim().toLowerCase().replace(/\s+/g, "_");
    const match = CASTE_OPTIONS.find(
        (o) =>
            o.value === normalized ||
            o.value === caste ||
            t(o.labelKey).toLowerCase() === String(caste).trim().toLowerCase()
    );
    return match ? t(match.labelKey) : caste;
};

// Get initial form values
export const getInitialValues = () => ({
    // Step 1
    customerType: "new",
    billNo: "",
    date: new Date(),
    referrerBillNo: "",
    loanType: "",
    name: "",
    mobileNumber: "",
    email: "",
    aadharCard: "",
    panCard: "",
    caste: "",
    occupation: "",
    company: "",
    address: "",
    DOB: null,
    borrowerId: "",
    profile: null,
    photo: null,
    // Step 3 (Loan Details)
    loanAmount: "",
    interestPerYear: "",
    loanStartDate: new Date(),
    durationYears: "",
    durationMonths: "",
    loanEndDate: null,
    existingCustomerId: "",
    interestType: "",
    monthlyInstallment: false,
    firstEMIDate: null,
    totalInstallments: "",
    monthlyInstallmentAmount: "",
    totalInterest: "",
    totalAmountPayable: "",
    totalEMI: "",
    principalReceived: "",
    processingCharge: "",
    processingChargeReceivedMode: "",
    processingChargeBankAccount: "",
    processingChargeTransactionNo: "",
    reminder: false,
    paymentType: "cash",
    bankAccount: "",
    chequeTransactionNo: "",
});

// Dynamic validation schema
export const getValidationSchema = (t) => {
    return Yup.object().shape({
        // Step 1
        customerType: Yup.string().required(t("customer.add.validation.customerTypeRequired")),
        date: Yup.date().required(t("customer.add.validation.dateRequired")),
        loanType: Yup.string().required(t("customer.add.validation.loanTypeRequired")),
        name: Yup.string().required(t("customer.add.validation.nameRequired")),
        mobileNumber: indianMobileSchema(t, {
            requiredMessage: t("customer.add.validation.mobileNumberRequired"),
            invalidMessage: t("customer.add.validation.mobileNumberInvalid"),
        }),
        email: Yup.string()
            .transform((value, originalValue) => (originalValue === "" ? null : value))
            .nullable()
            .email(t("customer.add.validation.emailInvalid")),
        aadharCard: Yup.string()
            .matches(AADHAR_CARD_REGEX, t("customer.add.validation.aadharCardInvalid"))
            .nullable(),
        panCard: Yup.string()
            .matches(PAN_CARD_REGEX, t("customer.add.validation.panCardInvalid"))
            .nullable(),
        // Step 3
        loanAmount: Yup.number()
            .min(0.01, t("customer.add.validation.loanAmountMin"))
            .required(t("customer.add.validation.loanAmountRequired")),
        interestPerYear: Yup.number()
            .min(0, t("customer.add.validation.interestMin"))
            .max(100, t("customer.add.validation.interestMax"))
            .required(t("customer.add.validation.interestRequired")),
        loanStartDate: Yup.date().required(t("customer.add.validation.loanStartDateRequired")),
        durationYears: Yup.number().required(t("customer.add.validation.durationYearsRequired")),
        durationMonths: Yup.number().required(t("customer.add.validation.durationMonthsRequired")),
        interestType: Yup.string().required(t("customer.add.validation.interestTypeRequired")),
        paymentType: Yup.string().required(t("customer.add.validation.paymentTypeRequired")),
        bankAccount: Yup.string().when("paymentType", {
            is: "bank",
            then: (schema) => schema.required(t("customer.add.validation.bankAccountRequired")),
            otherwise: (schema) => schema,
        }),
        chequeTransactionNo: Yup.string().when("paymentType", {
            is: "bank",
            then: (schema) => schema.required(t("customer.add.validation.chequeTransactionNoRequired")),
            otherwise: (schema) => schema,
        }),
        firstEMIDate: Yup.date().when("monthlyInstallment", {
            is: true,
            then: (schema) => schema.required(t("customer.add.validation.firstEMIDateRequired")),
            otherwise: (schema) => schema.nullable(),
        }),
        processingChargeReceivedMode: Yup.string().when("processingCharge", {
            is: (val) => val && val !== "" && parseFloat(val) > 0,
            then: (schema) => schema.required(t("customer.add.validation.processingChargeReceivedModeRequired")),
            otherwise: (schema) => schema,
        }),
        processingChargeBankAccount: Yup.string().when(
            ["processingCharge", "processingChargeReceivedMode"],
            {
                is: (processingCharge, processingChargeReceivedMode) =>
                    processingCharge && processingCharge !== "" && parseFloat(processingCharge) > 0 && processingChargeReceivedMode === "bank",
                then: (schema) => schema.required(t("customer.add.validation.processingChargeBankAccountRequired")),
                otherwise: (schema) => schema,
            }
        ),
        processingChargeTransactionNo: Yup.string().when(
            ["processingCharge", "processingChargeReceivedMode"],
            {
                is: (processingCharge, processingChargeReceivedMode) =>
                    processingCharge && processingCharge !== "" && parseFloat(processingCharge) > 0 && processingChargeReceivedMode === "bank",
                then: (schema) => schema.required(t("customer.add.validation.processingChargeTransactionNoRequired")),
                otherwise: (schema) => schema,
            }
        ),
    });
};

/** Yup schema scoped to the active wizard step so other steps are not validated early. */
export const getStepValidationSchema = (step, t) => {
    if (step === 4) return getValidationSchema(t);
    const fields = STEP_VALIDATION_FIELDS[step];
    if (!fields?.length) return Yup.object({});
    return getValidationSchema(t).pick(fields);
};

// Get collateral description for display
export const getCollateralDescription = (collateral) => {
    switch (collateral.type) {
        case "bankCheque":
            return `${collateral.chequeNumber || ""} - ${collateral.issuerBank || ""}`;
        case "goldJewellery":
            return `${collateral.weightGrams || ""}g${collateral.weightMilligrams ? ` ${collateral.weightMilligrams}mg` : ""} ${collateral.carat ? `(${collateral.carat}K)` : ""} ${collateral.description || ""}`.trim();
        case "silverJewellery":
            return `${collateral.weightGrams || ""}g${collateral.weightMilligrams ? ` ${collateral.weightMilligrams}mg` : ""} ${collateral.description || ""}`.trim();
        case "mobilePhone":
            return `${collateral.brand || ""} ${collateral.model || ""}`.trim();
        case "motorbike":
            return `${collateral.registrationNumber || ""} - ${collateral.model || ""}`.trim();
        case "others":
            return `${collateral.customTypeName || ""} ${collateral.description || ""}`.trim();
        default:
            return "-";
    }
};

// Get estimated value for display
export const getCollateralEstimatedValue = (collateral) => {
    return collateral.estimatedValue || collateral.value || "-";
};
